import { z } from 'zod';
import { paginationQuery } from './common.js';
import { documentLineInput, otherChargeInput, referenceInput } from './documents.js';
export const recurrenceFrequency = z.enum([
    'weekly',
    'biweekly',
    'monthly',
    'bimonthly',
    'quarterly',
    'semiannual',
    'yearly',
]);
export const FREQUENCY_LABEL = {
    weekly: 'Semanal',
    biweekly: 'Cada 15 días',
    monthly: 'Mensual',
    bimonthly: 'Bimestral',
    quarterly: 'Trimestral',
    semiannual: 'Semestral',
    yearly: 'Anual',
};
/** Suma un período a una fecha ISO (AAAA-MM-DD); los meses respetan el fin de mes. */
export function addPeriod(date, frequency) {
    const [y, m, d] = date.split('-').map(Number);
    const days = frequency === 'weekly' ? 7 : frequency === 'biweekly' ? 14 : 0;
    if (days) {
        const next = new Date(Date.UTC(y, m - 1, d + days));
        return next.toISOString().slice(0, 10);
    }
    const months = { monthly: 1, bimonthly: 2, quarterly: 3, semiannual: 6, yearly: 12 }[frequency];
    const target = new Date(Date.UTC(y, m - 1 + months, 1));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(d, lastDay));
    return target.toISOString().slice(0, 10);
}
const base = z.object({
    customerId: z.uuid('Elegí un cliente'),
    currency: z.enum(['CRC', 'USD', 'EUR']).default('CRC'),
    exchangeRate: z.coerce.number().positive().nullable().optional(),
    // Un solo medio de pago (el XML lleva solo el primero).
    paymentMethods: z
        .array(z.string().regex(/^\d{2}$/))
        .min(1)
        .max(1, 'Solo se admite un medio de pago')
        .default(['04']),
    saleCondition: z
        .string()
        .regex(/^\d{2}$/)
        .default('01'),
    creditDays: z.coerce.number().int().min(0).max(365).nullable().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    terms: z.string().trim().max(4000).nullable().optional(),
    /** Documento de referencia no electrónico (contrato, SICOP…): pasa a la factura que se emita. */
    reference: referenceInput.nullable().optional(),
    /** Actividad económica del receptor para la factura; vacío = la del cliente. */
    receiverActivity: z
        .string()
        .regex(/^\d{4,6}(\.\d)?$/)
        .nullable()
        .optional(),
    lines: z.array(documentLineInput).min(1, 'Agregá al menos una línea').max(1000),
    otherCharges: z.array(otherChargeInput).max(15).default([]),
    emailTo: z.array(z.email()).max(10).default([]),
});
export const quoteInput = base.extend({
    kind: z.literal('quote'),
    validUntil: z.iso.date().nullable().optional(),
    title: z.string().trim().max(160).nullable().optional(),
});
export const recurringInput = base
    .extend({
    kind: z.literal('recurring'),
    title: z.string().trim().min(2, 'Poné un nombre al plan').max(160),
    frequency: recurrenceFrequency,
    startDate: z.iso.date('Fecha inválida'),
    endDate: z.iso.date().nullable().optional(),
    documentKind: z.enum(['FE', 'TE']).default('FE'),
    /** Emitir y enviar a Hacienda solo; si no, se crea como borrador para revisión. */
    autoIssue: z.boolean().default(true),
})
    .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'La fecha final no puede ser anterior a la de inicio',
    path: ['endDate'],
});
export const quoteOrRecurringInput = z.discriminatedUnion('kind', [quoteInput, recurringInput]);
export const quoteStatus = z.enum([
    'draft',
    'sent',
    'accepted',
    'rejected',
    'converted',
    'active',
    'paused',
    'ended',
]);
export const QUOTE_STATUS_LABEL = {
    draft: 'Borrador',
    sent: 'Enviada',
    accepted: 'Aceptada',
    rejected: 'Rechazada',
    converted: 'Facturada',
    active: 'Activo',
    paused: 'Pausado',
    ended: 'Finalizado',
};
export const quoteListQuery = paginationQuery.extend({
    kind: z.enum(['quote', 'recurring']).default('quote'),
    status: quoteStatus.optional(),
    customerId: z.uuid().optional(),
});
//# sourceMappingURL=quotes.js.map